import React from 'react';
import { Zap, Check } from 'lucide-react';

/**
 * Inline status chip for in-progress tool calls / pipeline phases.
 *
 * Two states:
 *   - 'running': pulsing lightning icon, subtle shimmer across the chip body
 *   - 'done':    static check icon, dimmed text, no animation
 *
 * Non-collapsible by design (v1). Animation is pure CSS so it survives
 * React re-renders without flicker.
 */
export default function StatusChip({ label, state = 'running', icon: IconOverride = null }) {
  const isRunning = state === 'running';
  const Icon = IconOverride || (isRunning ? Zap : Check);

  return (
    <div
      className={
        'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ' +
        (isRunning
          ? 'border-violet-200 bg-violet-50 text-violet-900 dark:border-violet-900 dark:bg-violet-950/30 dark:text-violet-200'
          : 'border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-400')
      }
      style={isRunning ? { position: 'relative', overflow: 'hidden' } : undefined}
    >
      {isRunning && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, transparent 0%, rgba(139, 92, 246, 0.08) 40%, rgba(139, 92, 246, 0.18) 50%, rgba(139, 92, 246, 0.08) 60%, transparent 100%)',
            backgroundSize: '200% 100%',
            animation: 'statusChipShimmer 2.4s linear infinite',
            pointerEvents: 'none',
          }}
        />
      )}
      <Icon
        className={
          'w-3.5 h-3.5 flex-shrink-0 ' +
          (isRunning ? 'text-violet-600 dark:text-violet-400' : 'text-slate-400')
        }
        style={isRunning ? { animation: 'statusChipPulse 1.4s ease-in-out infinite' } : undefined}
      />
      <span style={{ position: 'relative', zIndex: 1 }}>{label}</span>
      <style>{`
        @keyframes statusChipShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes statusChipPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}
